import { useEffect, useRef, useState } from "react";

// Esperas entre reintentos cuando la conexión queda CLOSED (respuesta no-200,
// content-type incorrecto, 401...). EventSource no reintenta en esos casos.
export const SSE_BACKOFF_MS = [2000, 5000, 15000, 30000, 60000];
export const SSE_JITTER_RATIO = 0.2;
// Más que SSE_MAX_AGE del backend (20 s): si la app estuvo en segundo plano
// más que esto, la conexión que haya quedado ya no es confiable.
export const SSE_HIDDEN_REOPEN_MS = 25000;
const TOKEN_EXPIRY_MARGIN_MS = 30000;

export const backoffDelay = (attempt) => {
  const base = SSE_BACKOFF_MS[Math.min(attempt, SSE_BACKOFF_MS.length - 1)];
  const jitter = 1 + (Math.random() * 2 - 1) * SSE_JITTER_RATIO;
  return Math.round(base * jitter);
};

const decodeJwtExp = (token) => {
  try {
    const [, payload] = String(token || "").split(".");
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof json?.exp === "number" ? json.exp * 1000 : null;
  } catch {
    return null;
  }
};

export const isTokenExpiring = (token, now = Date.now()) => {
  const exp = decodeJwtExp(token);
  return exp !== null && exp - now < TOKEN_EXPIRY_MARGIN_MS;
};

/**
 * EventSource que se recupera solo.
 *
 * - El corte normal del servidor (cada SSE_MAX_AGE) lo reconecta el propio
 *   EventSource (readyState CONNECTING); no se toca.
 * - Si queda CLOSED, reintenta con backoff + jitter (2 s, 5 s, 15 s, 30 s, 60 s).
 * - El token viaja en la URL: antes de cada conexión, si está por vencer, se
 *   refresca. Si el refresh falla, deja de reintentar y llama a onAuthExpired.
 * - Al volver la app al frente o recuperar la red, reabre enseguida.
 *
 * onOpen recibe { recovered }: true cuando la conexión vuelve después de un
 * corte que pudo perder eventos (backoff, refresh, vuelta al frente).
 */
export default function useResilientEventSource({
  enabled = true,
  connectionKey = "",
  buildUrl,
  getToken,
  refreshToken,
  onAuthExpired,
  handlers = {},
  onOpen,
}) {
  const [status, setStatus] = useState("idle");
  const callbacksRef = useRef({});
  callbacksRef.current = { buildUrl, getToken, refreshToken, onAuthExpired, handlers, onOpen };

  useEffect(() => {
    if (!enabled || typeof window === "undefined" || typeof window.EventSource === "undefined") {
      setStatus("idle");
      return undefined;
    }

    let source = null;
    let sourceToken = "";
    let retryTimer = null;
    let attempt = 0;
    let recovered = false;
    let disposed = false;
    let hiddenAt = null;

    const update = (next) => {
      if (!disposed) setStatus(next);
    };

    const closeSource = () => {
      if (source) {
        source.onopen = null;
        source.onerror = null;
        source.close();
        source = null;
      }
    };

    const stop = () => {
      closeSource();
      clearTimeout(retryTimer);
      update("stopped");
      callbacksRef.current.onAuthExpired?.();
    };

    const resolveToken = async () => {
      const { getToken: readToken, refreshToken: renew } = callbacksRef.current;
      const current = readToken?.() || "";
      if (current && !isTokenExpiring(current)) return current;
      if (!renew) return current || null;
      try {
        return (await renew()) || null;
      } catch {
        return null;
      }
    };

    const scheduleRetry = () => {
      closeSource();
      clearTimeout(retryTimer);
      recovered = true;
      update("retrying");
      const delay = backoffDelay(attempt);
      attempt += 1;
      retryTimer = setTimeout(connect, delay);
    };

    async function connect() {
      if (disposed) return;
      clearTimeout(retryTimer);
      closeSource();
      update("connecting");

      const token = await resolveToken();
      if (disposed) return;
      if (!token) {
        stop();
        return;
      }

      const url = callbacksRef.current.buildUrl?.(token);
      if (!url) {
        update("idle");
        return;
      }

      const es = new window.EventSource(url);
      source = es;
      sourceToken = token;

      es.onopen = () => {
        attempt = 0;
        const wasRecovered = recovered;
        recovered = false;
        update("open");
        callbacksRef.current.onOpen?.({ recovered: wasRecovered });
      };

      es.onerror = () => {
        if (es !== source) return;
        if (es.readyState === window.EventSource.CLOSED) {
          scheduleRetry();
          return;
        }
        // Reconexión nativa en curso: reusaría la URL con el token viejo.
        if (isTokenExpiring(sourceToken)) {
          recovered = true;
          connect();
          return;
        }
        update("connecting");
      };

      Object.keys(callbacksRef.current.handlers || {}).forEach((eventName) => {
        es.addEventListener(eventName, (event) => {
          callbacksRef.current.handlers?.[eventName]?.(event);
        });
      });
    }

    const reopenNow = () => {
      attempt = 0;
      recovered = true;
      connect();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        return;
      }
      const hiddenFor = hiddenAt ? Date.now() - hiddenAt : 0;
      hiddenAt = null;
      const alive = source && source.readyState === window.EventSource.OPEN;
      if (!alive || hiddenFor >= SSE_HIDDEN_REOPEN_MS) reopenNow();
    };

    const onOnline = () => {
      if (!source || source.readyState !== window.EventSource.OPEN) reopenNow();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("online", onOnline);
    connect();

    return () => {
      disposed = true;
      clearTimeout(retryTimer);
      closeSource();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("online", onOnline);
    };
  }, [enabled, connectionKey]);

  return { status };
}
